/**
 * resizeObserverFix
 * -----------------
 * Coalesce every ResizeObserver callback into the next animation frame.
 *
 * Chrome emits a (benign) "ResizeObserver loop completed with undelivered
 * notifications" error whenever an observer callback mutates layout in a way
 * that would schedule another resize within the same frame. The browser just
 * defers the extra work — nothing actually breaks — but webpack-dev-server's
 * overlay surfaces it as a full-screen runtime error.
 *
 * We hit it constantly in the lesson views: Monaco runs with
 * `automaticLayout: true` (which observes its container) inside react-resizable
 * panes, so dragging a splitter resizes the container → Monaco relayouts →
 * another resize is scheduled mid-frame → loop warning.
 *
 * Deferring the real callback to requestAnimationFrame means the synchronous
 * observation step does no layout work, so the browser never has an
 * "undelivered" loop to report. The actual relayout happens one frame later,
 * which is imperceptible. This fixes it in development AND production, instead
 * of relying on swallowing the error in the dev overlay.
 */
const Native =
  typeof window !== "undefined" ? window.ResizeObserver : undefined;

if (Native && !Native.__rafPatched) {
  class RafResizeObserver extends Native {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || entries.length === 0) return;
          callback(entries, observer);
        });
      });
    }
  }

  RafResizeObserver.__rafPatched = true;
  window.ResizeObserver = RafResizeObserver;
}

export {};
